// Pure-JS text analysis primitives used by scoring rules.
// No external deps; deterministic; safe for browser.

export interface TextMetrics {
  charCount: number;
  wordCount: number;
  sentenceCount: number;
  paragraphCount: number;
  avgWordsPerSentence: number;
  avgWordsPerParagraph: number;
  estimatedTokens: number;
  imperativeRatio: number;
  passiveCount: number;
  passiveRatio: number;
  hedgeCount: number;
  fillerCount: number;
  firstPersonCount: number;
  exclamationCount: number;
  questionCount: number;
  uniqueLowercaseTokenRatio: number;
  longestSentenceWords: number;
}

const HEDGES = [
  'maybe','perhaps','possibly','probably','might','could','sometimes','often','usually',
  'generally','typically','arguably','seems','appears','tends to','kind of','sort of',
  'a bit','somewhat','fairly','quite','rather',
];

const FILLERS = [
  'really','very','basically','essentially','just','simply','actually','literally',
  'truly','obviously','clearly','definitely','absolutely','totally','completely','quite',
  'pretty','sort of','kind of','various','several','many','some',
];

// Imperative-mood openers (case-insensitive, line start)
const IMPERATIVE_VERBS = [
  'use','run','call','invoke','create','generate','make','build','add','remove',
  'delete','update','set','get','fetch','load','save','write','read','open','close',
  'check','verify','validate','test','review','audit','inspect','analyze','parse',
  'convert','transform','translate','format','render','send','post','push','pull',
  'install','configure','deploy','start','stop','restart','enable','disable',
  'monitor','track','log','print','echo','copy','paste','move','rename',
  'split','join','merge','filter','map','reduce','sort','find','search','replace',
  'do','don\'t','ask','confirm','prompt','wait','retry','skip','fail','return',
  'first','then','next','finally','always','never','if','when','before','after',
  'ensure','make sure','remember','note','important','warning','critical','required',
];

const PASSIVE = /\b(?:is|are|was|were|be|been|being|am|got|gets|gotten)\s+(?:[a-z]+ly\s+)?[a-z]+ed\b/gi;
const FIRST_PERSON = /\b(?:i|we|me|my|us|our|ours)\b/gi;

export function tokenize(text: string): string[] {
  return text.toLowerCase().replace(/[^a-z0-9'\s-]/g, ' ').split(/\s+/).filter(Boolean);
}

export function splitSentences(text: string): string[] {
  // Strip code fences before sentence-splitting; they confuse counts.
  const stripped = text.replace(/```[\s\S]*?```/g, '');
  // Conservative split on .!? followed by whitespace and capital/digit, plus newline.
  return stripped
    .split(/(?<=[.!?])\s+(?=[A-Z0-9"'`(\[])|\n{2,}/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function splitParagraphs(text: string): string[] {
  return text
    .replace(/```[\s\S]*?```/g, '')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0 && !/^#{1,6}\s/.test(p));
}

export function estimateTokens(text: string): number {
  // Rough estimate aligned with tiktoken averages for English markdown.
  // 1 token ≈ 0.75 words ≈ 4 chars.
  return Math.ceil(text.length / 4);
}

function countMatches(text: string, words: string[]): number {
  let count = 0;
  const lower = text.toLowerCase();
  for (const w of words) {
    const re = new RegExp(`\\b${w.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'g');
    const m = lower.match(re);
    if (m) count += m.length;
  }
  return count;
}

function isImperativeSentence(s: string): boolean {
  // First non-list-marker word is an imperative verb.
  const cleaned = s.replace(/^[#>\-*\d.\s)`]+/, '').trim().toLowerCase();
  if (!cleaned) return false;
  const first = cleaned.split(/[\s,:;]+/)[0];
  if (!first) return false;
  return IMPERATIVE_VERBS.includes(first);
}

export function analyzeText(text: string): TextMetrics {
  const sentences = splitSentences(text);
  const paragraphs = splitParagraphs(text);
  const words = tokenize(text);
  const wordCount = words.length;

  const imperativeCount = sentences.filter(isImperativeSentence).length;
  const imperativeRatio = sentences.length === 0 ? 0 : imperativeCount / sentences.length;

  const passiveMatches = text.match(PASSIVE) ?? [];
  const passiveCount = passiveMatches.length;
  const passiveRatio = sentences.length === 0 ? 0 : passiveCount / sentences.length;

  const hedgeCount = countMatches(text, HEDGES);
  const fillerCount = countMatches(text, FILLERS);
  const firstPersonCount = (text.match(FIRST_PERSON) ?? []).length;

  const longestSentenceWords = sentences.reduce((acc, s) => {
    const len = tokenize(s).length;
    return len > acc ? len : acc;
  }, 0);

  const unique = new Set(words).size;
  const uniqueLowercaseTokenRatio = wordCount === 0 ? 0 : unique / wordCount;

  return {
    charCount: text.length,
    wordCount,
    sentenceCount: sentences.length,
    paragraphCount: paragraphs.length,
    avgWordsPerSentence: sentences.length === 0 ? 0 : wordCount / sentences.length,
    avgWordsPerParagraph: paragraphs.length === 0 ? 0 : wordCount / paragraphs.length,
    estimatedTokens: estimateTokens(text),
    imperativeRatio,
    passiveCount,
    passiveRatio,
    hedgeCount,
    fillerCount,
    firstPersonCount,
    exclamationCount: (text.match(/!/g) ?? []).length,
    questionCount: (text.match(/\?/g) ?? []).length,
    uniqueLowercaseTokenRatio,
    longestSentenceWords,
  };
}
